import {
  Button,
  Checkbox,
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxItem,
  ComboboxSearchField,
  ComboboxTrigger,
  ComboboxTriggerLoading,
  ComboboxValue,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  InputMask,
  masks,
} from '@barfacil/barfacil-ui'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DraggableSyntheticListeners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CellContext,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Check, CircleSlash2, Ellipsis, Move, X } from 'lucide-react'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'

import {
  CategoryOptionsResponse,
  Image,
  PriceListProductsResponse,
  Product,
  Products,
} from '@/api/_schemas/management'
import { DeleteProductDialog } from '@/app/panel/management/price-list/_components/delete-product-dialog'
import { PriceListProductImageDialog } from '@/app/panel/management/price-list/_components/price-list-product-image-dialog'
import { UpdateProductDialog } from '@/app/panel/management/price-list/_components/update-product-dialog'
import { UpdateProductInLineDialog } from '@/app/panel/management/price-list/_components/update-product-in-line-dialog'
import { UpdateProductTaxDialog } from '@/app/panel/management/price-list/_components/update-product-tax-dialog'
import { usePriceListContext } from '@/app/panel/management/price-list/_contexts/price-list-context'
import { UpdateProductContextProvider } from '@/app/panel/management/price-list/_contexts/update-product-context'
import { usePriceListCategoryOptions } from '@/app/panel/management/price-list/_hooks/use-category-options'
import { useCreatePriceListProduct } from '@/app/panel/management/price-list/_hooks/use-create-price-list-product'
import { usePriceListProducts } from '@/app/panel/management/price-list/_hooks/use-price-list-products'
import { usePriceListProductOptions } from '@/app/panel/management/price-list/_hooks/use-product-options'
import {
  FormCreatePriceListProduct,
  FormCreatePriceListProductSchema,
} from '@/app/panel/management/price-list/_schemas/create-price-list-product'
import {
  Table,
  TableAlert,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from '@/components/ui/table'
import { useFormErrorHandler } from '@/hooks/use-form-error-handler'
import {
  cn,
  formatToNumber,
  getDefaultsFromZod,
  removeAccents,
} from '@/lib/utils'
import { CustomCellContext } from '@/schemas/table'

import { useUpdatePriceListProductPosition } from '../_hooks/use-update-product-position'

type PriceListProductsTableProps = {
  id: number
}

type PriceListNewProductProps = PriceListProductsTableProps & {
  isPending: boolean
  isSuccess: boolean
}

type PriceListProduct = PriceListProductsResponse[0]

type PriceListProductsTableCheckboxProps = {
  data: PriceListProductsResponse[0]
}

type PriceListNewProductActionsProps = {
  onClear: () => void
}

type PriceListActionsProps = {
  priceListId: number
  priceListProductId: number
  product: Product
  isInactive: boolean
  listeners: DraggableSyntheticListeners
  attributes: React.HTMLAttributes<HTMLElement>
}

function PriceListNewProductActions({
  onClear,
}: PriceListNewProductActionsProps) {
  const form = useFormContext<FormCreatePriceListProduct>()

  function handleOnClear() {
    form.reset()
    onClear()
  }

  return (
    <div className="flex items-center justify-end gap-1 text-sky-500">
      <Button
        className="size-6"
        variant="ghost"
        size="icon"
        type="button"
        disabled
      >
        <Ellipsis size={20} />
      </Button>

      <Button className="size-6" variant="ghost" size="icon" type="button">
        <X size={24} onClick={handleOnClear} />
      </Button>

      <Button
        className="size-6"
        variant="ghost"
        size="icon"
        type="submit"
        form="price-list-new-product-form"
      >
        <Check className="text-sky-500" size={24} />
      </Button>

      <Button
        className="h-16 w-6 bg-gray-100"
        variant="ghost"
        size="icon"
        type="button"
        disabled
      >
        <Move size={24} />
      </Button>
    </div>
  )
}

function PriceListActions({
  priceListId,
  priceListProductId,
  product,
  isInactive,
  attributes,
  listeners,
}: PriceListActionsProps) {
  return (
    <div className="flex items-center justify-end gap-1 text-sky-500">
      <aside className="flex items-center gap-1">
        <Button className="h-8 w-14 p-0" size="sm" disabled>
          Limite
        </Button>
        {product.tipo !== 3 && (
          <UpdateProductTaxDialog product={product} isInactive={isInactive} />
        )}
      </aside>

      <Button className="size-6" variant="ghost" size="icon" disabled>
        <CircleSlash2 size={20} />
      </Button>

      <DeleteProductDialog
        priceListProductId={priceListProductId}
        name={product.nome}
      />

      <UpdateProductContextProvider value={{ priceListId, product }}>
        <UpdateProductDialog />
      </UpdateProductContextProvider>

      <Button
        className="h-16 w-6 touch-none bg-sky-25 hover:bg-sky-50"
        variant="ghost"
        size="icon"
        {...attributes}
        {...listeners}
      >
        <Move size={24} />
      </Button>
    </div>
  )
}

function PriceListProductsTableCheckbox({
  data,
}: PriceListProductsTableCheckboxProps) {
  const { selectedValues, toggleSelectedValue } = usePriceListContext()

  const isInactive = data.situacao === 0
  const isCombo = data.produto.tipo === 3

  return (
    <div className="flex items-center space-x-2">
      <div
        className={cn(
          'h-[102px] w-[10px]',
          isInactive ? 'bg-gray-300' : isCombo ? 'bg-yellow-500' : '',
        )}
      />

      <Checkbox
        disabled={isInactive}
        checked={
          !!selectedValues.find((id) => data.cod_produto_lista_preco === id)
        }
        onCheckedChange={() =>
          toggleSelectedValue(data.cod_produto_lista_preco)
        }
      />
    </div>
  )
}

function PriceListNewProduct({
  id,
  isPending,
  isSuccess,
}: PriceListNewProductProps) {
  const [openComboboxProduct, setOpenComboboxProduct] = useState(false)
  const [openComboboxCategory, setOpenComboboxCategory] = useState(false)

  const [productSearch, setProductSearch] = useState('')
  const [categorySearch, setCategorySearch] = useState('')

  const [filteredProducts, setFilteredProducts] = useState<Products>([])
  const [filteredCategories, setFilteredCategories] =
    useState<CategoryOptionsResponse>([])

  const [selectedImage, setSelectedImage] = useState<Image>(null)

  const { data: productOptions, isLoading: isLoadingProductOptions } =
    usePriceListProductOptions(id)
  const { data: categoryOptions, isLoading: isLoadingCategoryOptions } =
    usePriceListCategoryOptions()

  const form = useFormContext<FormCreatePriceListProduct>()

  function handleProductSearchChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const value = event.currentTarget.value

    setProductSearch(value)
    form.setValue('produto', value, { shouldValidate: true })

    setFilteredProducts(
      productOptions?.filter(({ nome }) =>
        removeAccents(nome.toUpperCase()).includes(
          removeAccents(value.toUpperCase()),
        ),
      ) ?? [],
    )
  }

  function handleCategorySearchChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const value = event.currentTarget.value

    setCategorySearch(value)
    form.setValue('categoria', value)

    setFilteredCategories(
      categoryOptions?.filter(({ nome }) =>
        removeAccents(nome.toUpperCase()).includes(
          removeAccents(value.toUpperCase()),
        ),
      ) ?? [],
    )
  }

  function handleOnSelectProduct(
    value: string,
    image: string | null,
    imagePath: string | null,
    categoryName?: string,
  ) {
    if (categoryName)
      form.setValue('categoria', categoryName, { shouldValidate: true })

    const hasImage = !!image && !!imagePath
    const newImage: Image = hasImage
      ? {
          descricao: value,
          imagem: image,
          imagem_path: imagePath,
        }
      : null

    setSelectedImage(newImage)
    form.setValue('imagem', hasImage ? image : undefined)
    form.setValue('produto', value, { shouldValidate: true })
  }

  function handleOnSelectCategory(value: string) {
    form.setValue('categoria', value, { shouldValidate: true })
  }

  function clearSelectedImage() {
    setSelectedImage(null)
    form.setValue('imagem', undefined)
  }

  function handleOnSave(image: Image) {
    setSelectedImage(image)
    form.setValue('imagem', image?.imagem)
  }

  useEffect(() => {
    if (!isSuccess) return

    setSelectedImage(null)
  }, [isSuccess])

  return (
    <TableRow>
      <TableCell />

      <TableCell className="min-w-64 max-w-64 px-2">
        <FormField
          control={form.control}
          name="produto"
          render={({ field }) => (
            <FormItem>
              <Combobox
                open={openComboboxProduct}
                onOpenChange={setOpenComboboxProduct}
                onValueChange={field.onChange}
                defaultValue={field.value}
                focusSelected={false}
                disabled={isPending}
              >
                <FormControl>
                  {isLoadingProductOptions ? (
                    <ComboboxTriggerLoading>
                      Informe o nome do produto
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger
                      id="price-list-new-product-combobox"
                      onBlur={() => setProductSearch('')}
                    >
                      <ComboboxValue placeholder="Informe o nome do produto">
                        {field.value}
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={productSearch}
                    autoComplete="off"
                    loading={isLoadingProductOptions}
                    onChange={handleProductSearchChange}
                    onBlur={(e) => {
                      if (e.target.value) clearSelectedImage()
                    }}
                    onKeyDown={(e) => {
                      if (!['Escape', 'Enter'].includes(e.key)) return
                      if (e.key === 'Enter') setOpenComboboxProduct(false)
                      if (e.currentTarget.value) clearSelectedImage()
                    }}
                  />

                  <ComboboxGroup>
                    {(!productSearch ? productOptions : filteredProducts)?.map(
                      ({
                        cod_produto: id,
                        nome,
                        produto_pai: category,
                        imagem: image,
                        imagem_path: imagePath,
                      }) => (
                        <ComboboxItem
                          key={id}
                          value={nome}
                          onClick={() => {
                            handleOnSelectProduct(
                              nome,
                              image,
                              imagePath,
                              category?.nome,
                            )
                            setOpenComboboxProduct(false)
                          }}
                        >
                          {nome}
                        </ComboboxItem>
                      ),
                    )}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>

              <FormMessage className="absolute w-max" />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="w-64 min-w-64 max-w-64 px-2">
        <FormField
          control={form.control}
          name="categoria"
          render={({ field }) => (
            <FormItem>
              <Combobox
                open={openComboboxCategory}
                onOpenChange={setOpenComboboxCategory}
                onValueChange={field.onChange}
                defaultValue={field.value}
                focusSelected={false}
                disabled={isPending}
              >
                <FormControl>
                  {isLoadingCategoryOptions ? (
                    <ComboboxTriggerLoading>
                      Informe o nome da categoria
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger onBlur={() => setCategorySearch('')}>
                      <ComboboxValue placeholder="Informe o nome da categoria">
                        {field.value}
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={categorySearch}
                    autoComplete="off"
                    loading={isLoadingCategoryOptions}
                    onChange={handleCategorySearchChange}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return
                      setOpenComboboxCategory(false)
                    }}
                  />

                  <ComboboxGroup>
                    {(!categorySearch
                      ? categoryOptions
                      : filteredCategories
                    )?.map(({ cod_produto: id, nome }) => (
                      <ComboboxItem
                        key={id}
                        value={nome}
                        onClick={() => {
                          handleOnSelectCategory(nome)
                          setOpenComboboxCategory(false)
                        }}
                      >
                        {nome}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>

              <FormMessage className="absolute w-max" />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="w-32 min-w-32 max-w-32 px-2 2xl:w-52 2xl:min-w-52 2xl:max-w-52">
        <FormField
          control={form.control}
          name="valor"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputMask
                  {...field}
                  placeholder="R$"
                  mask="currency"
                  disabled={isPending}
                  form="price-list-new-product-form"
                />
              </FormControl>

              <FormMessage className="absolute w-max" />
            </FormItem>
          )}
        />
      </TableCell>

      <TableCell className="w-16 min-w-16 max-w-16">
        <PriceListProductImageDialog
          product={{
            produto: form.getValues('produto') || undefined,
          }}
          image={selectedImage}
          onSave={handleOnSave}
        />
      </TableCell>

      <TableCell className="w-60 min-w-60 max-w-60">
        <PriceListNewProductActions onClear={() => setSelectedImage(null)} />
      </TableCell>
    </TableRow>
  )
}

function SortableRow({
  row,
  selectedValues,
}: {
  row: Row<PriceListProduct>
  selectedValues: number[]
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: row.original.cod_produto_lista_preco.toString(),
    })

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  }

  return (
    <TableRow
      key={row.id}
      ref={setNodeRef}
      style={style}
      aria-checked={
        !!selectedValues.find(
          (id) => row.original.cod_produto_lista_preco === id,
        )
      }
    >
      {row.getVisibleCells().map((cell, index) => (
        <TableCell
          key={cell.id}
          className={cn(
            '[&:has([role=checkbox])]:p-0',
            [1, 2, 3].includes(index) && 'px-2',
          )}
        >
          {flexRender(cell.column.columnDef.cell, {
            ...cell.getContext(),
            attributes: cell.column.id === 'actions' ? attributes : undefined,
            listeners: cell.column.id === 'actions' ? listeners : undefined,
          })}
        </TableCell>
      ))}
    </TableRow>
  )
}

function PriceListProductsTable({
  id: priceListId,
}: PriceListProductsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])

  const { selectedValues } = usePriceListContext()
  const sensors = useSensors(useSensor(PointerSensor))

  const { data, isLoading, isFetching, isError } =
    usePriceListProducts(priceListId)
  const { mutation, formErrors } = useCreatePriceListProduct()
  const { mutation: updateProductPositionMutation } =
    useUpdatePriceListProductPosition(priceListId)

  const columns = useMemo<ColumnDef<PriceListProduct>[]>(
    () => [
      {
        accessorKey: 'checkbox',
        header: () => <></>,
        cell: ({ row: { original } }) => (
          <PriceListProductsTableCheckbox data={original} />
        ),
      },
      {
        accessorKey: 'name',
        header: () => <span>PRODUTO</span>,
        cell: ({
          row: {
            original: {
              cod_produto_lista_preco: id,
              produto: {
                nome: produto,
                produto_pai: categoria,
                imagem: image,
                tipo,
              },
              situacao,
              vlr_produto: valor,
            },
          },
        }) => (
          <UpdateProductInLineDialog
            formKey="produto"
            formValue={{
              produto,
              categoria: categoria?.nome as string,
              valor: valor.toString(),
              imagem: image ?? undefined,
            }}
            id={id}
            priceListId={priceListId}
            isInactive={situacao === 0}
            isCombo={tipo === 3}
          />
        ),
      },
      {
        accessorKey: 'category',
        header: () => <span>CATEGORIA</span>,
        cell: ({
          row: {
            original: {
              cod_produto_lista_preco: id,
              produto: {
                nome: produto,
                produto_pai: categoria,
                imagem: image,
                tipo,
              },
              situacao,
              vlr_produto: valor,
            },
          },
        }) => (
          <UpdateProductInLineDialog
            formKey="categoria"
            formValue={{
              produto,
              categoria: categoria?.nome as string,
              valor: valor.toString(),
              imagem: image ?? undefined,
            }}
            id={id}
            priceListId={priceListId}
            isInactive={situacao === 0}
            isCombo={tipo === 3}
          />
        ),
      },
      {
        accessorKey: 'value',
        header: () => <span>VALOR</span>,
        cell: ({
          row: {
            original: {
              cod_produto_lista_preco: id,
              produto: {
                nome: produto,
                produto_pai: categoria,
                imagem: image,
                tipo,
              },
              situacao,
              vlr_produto: valor,
            },
          },
        }) => {
          const isInactive = situacao === 0
          const isCombo = tipo === 3

          if (isCombo && !isInactive) {
            return (
              <div className="h-10 py-2 pl-3 pr-4">
                <span>{masks.currency(Number(valor).toFixed(2))}</span>
              </div>
            )
          }

          return (
            <UpdateProductInLineDialog
              formKey="valor"
              formValue={{
                produto,
                categoria: categoria?.nome as string,
                valor: valor.toString(),
                imagem: image ?? undefined,
              }}
              id={id}
              priceListId={priceListId}
              isInactive={isInactive}
              isCombo={isCombo}
            />
          )
        },
      },
      {
        accessorKey: 'image',
        header: () => <span>IMAGEM</span>,
        cell: ({
          row: {
            original: {
              cod_produto_lista_preco: id,
              produto: {
                cod_produto: productId,
                nome: produto,
                imagem_path: imagePath,
                imagem,
                produto_pai: categoria,
              },
              situacao,
              vlr_produto: valor,
            },
          },
        }) => (
          <PriceListProductImageDialog
            id={id}
            priceListId={priceListId}
            image={
              imagePath && imagem
                ? { descricao: produto, imagem_path: imagePath, imagem }
                : null
            }
            isInactive={situacao === 0}
            product={{
              id: productId,
              produto,
              valor: valor.toString(),
              categoria: categoria?.nome,
            }}
          />
        ),
      },
      {
        accessorKey: 'actions',
        header: () => <></>,
        cell: (ctx: CellContext<PriceListProduct, unknown>) => {
          const { row, attributes, listeners } =
            ctx as unknown as CustomCellContext<PriceListProduct, unknown>
          const { original } = row
          const {
            produto,
            situacao,
            cod_produto_lista_preco: priceListProductId,
          } = original

          return (
            <PriceListActions
              priceListId={priceListId}
              priceListProductId={priceListProductId}
              product={produto}
              isInactive={situacao === 0}
              attributes={attributes}
              listeners={listeners}
            />
          )
        },
      },
    ],
    [priceListId],
  )

  const table = useReactTable({
    data: data ?? [],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  })

  const form = useForm<FormCreatePriceListProduct>({
    resolver: zodResolver(FormCreatePriceListProductSchema),
    defaultValues: getDefaultsFromZod(FormCreatePriceListProductSchema),
  })

  useFormErrorHandler({
    form,
    formErrors,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
  })

  function handleSubmit<T>(e: FormEvent<T>) {
    e.preventDefault()

    return form.handleSubmit((data) => {
      const parsedData = {
        ...data,
        valor: formatToNumber(data.valor),
      } as FormCreatePriceListProduct
      mutation.mutate(
        { ...parsedData, id: priceListId },
        {
          onSuccess: () => form.reset(),
        },
      )
    })()
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!data || !over || active.id === over.id) return

    const fromIndex = data.findIndex(
      (item) => item.cod_produto_lista_preco === Number(active.id),
    )
    const toIndex = data.findIndex(
      (item) => item.cod_produto_lista_preco === Number(over.id),
    )

    if (fromIndex === toIndex) return

    const movedItem = data[fromIndex]

    updateProductPositionMutation.mutate({
      id: movedItem.cod_produto_lista_preco,
      priceListId,
      posicao: toIndex,
      fromIndex,
    })
  }

  if (isFetching || isLoading) {
    return <TableLoading rows={5} columns={columns.length} />
  }

  if (isError) {
    return <TableAlert>Ocorreu um erro ao buscar os produtos.</TableAlert>
  }

  return (
    <Form {...form}>
      <form id="price-list-new-product-form" onSubmit={handleSubmit} />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={
            data?.map((item) => item.cod_produto_lista_preco.toString()) ?? []
          }
          strategy={verticalListSortingStrategy}
        >
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        className="h-12 bg-blue-400 text-gray-25"
                        key={header.id}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              <PriceListNewProduct
                id={priceListId}
                isPending={mutation.isPending}
                isSuccess={mutation.isSuccess}
              />

              {table.getRowModel().rows.map((row) => (
                <SortableRow
                  key={row.id}
                  row={row}
                  selectedValues={selectedValues}
                />
              ))}
            </TableBody>
          </Table>
        </SortableContext>
      </DndContext>
    </Form>
  )
}

export { PriceListProductsTable }
